import { Period } from "@/@types/generics/Period";
import { HealthUnit } from "@/@types/sintegrada/HealthUnit";
import { RiskClassification } from "@/@types/sintegrada/RiskClassification";
import { User } from "@/@types/sintegrada/User";
import { getSintegradaServerApi } from "@/services/api";
import { ResumeGrouped } from "@/services/esus/attendanceService";

export type RiskClassificationDashboardParams = {
	uf: string;
	ibgeCode: string;
	startDate: string;
	endDate: string;
	cnes?: string;
	youngerAge?: number;
	olderAge?: number;
	professionalId?: number;
	professionalCns?: string;
	professionalName?: string;
	minAge: number | null;
	maxAge: number | null;
};

export type AttendanceTimeDetails = {
	attendance_id: number;
	patient_name: string;
	patient_cpf: string;
	attendance_date: string;
	risk_classification_name: string;
	risk_classification_color: string;
	time_in_seconds?: number;
	wait_time_in_seconds?: number;
};

export type RiskClassificationDashboardData = {
	aggregatedByRiskClassification: ResumeGrouped<RiskClassification, object>[];
	aggregatedByPeriodAndRiskClassificationStatus: ResumeGrouped<
		Period,
		ResumeGrouped<boolean, object>
	>[];
	aggregatedByHealthUnitAndRiskClassification: ResumeGrouped<
		HealthUnit,
		ResumeGrouped<RiskClassification, object>
	>[];
	aggregatedByGender: ResumeGrouped<"M" | "F", null>[];
	averageTimeInSecondsByRiskClassification: ResumeGrouped<RiskClassification, object>[];
	averageWaitTimeInSecondsByRiskClassification: ResumeGrouped<RiskClassification, object>[];
	aggregatedByAttendanceConditions: ResumeGrouped<string, object>[];
	aggregatedByProfessionalsAndRiskClassificationStatus: ResumeGrouped<
		User,
		ResumeGrouped<"Reclassificações" | "Classificações", object>
	>[];
	averageTimeInSecondsByProfessional: ResumeGrouped<User, object>[];
	averageTimeInSeconds: number;
	averageWaitTimeInSeconds: number;
	maxTimeInSeconds: number;
	minTimeInSeconds: number;
	maxWaitTimeInSeconds: number;
	minWaitTimeInSeconds: number;
	maxTimeAttendanceDetails: AttendanceTimeDetails;
	minTimeAttendanceDetails: AttendanceTimeDetails;
	maxWaitTimeAttendanceDetails: AttendanceTimeDetails;
	minWaitTimeAttendanceDetails: AttendanceTimeDetails;
	evasions: number;
	totalAttendancesUnclassified: number;
	totalAttendancesClassifiedOnce: number;
	totalAttendancesClassifiedMoreThanOnce: number;
	totalAttendances: number;
};

export async function getRiskClassificationDashboard({
	...params
}: RiskClassificationDashboardParams) {
	const url = `/attendance/risk-classification-dashboard`;

	const { data } = await getSintegradaServerApi().get<RiskClassificationDashboardData>(
		url,
		params
	);

	// Garantir que todas as propriedades sejam arrays válidos
	const defaultData: RiskClassificationDashboardData = {
		aggregatedByRiskClassification: [],
		aggregatedByPeriodAndRiskClassificationStatus: [],
		aggregatedByHealthUnitAndRiskClassification: [],
		aggregatedByGender: [],
		averageTimeInSecondsByRiskClassification: [],
		averageWaitTimeInSecondsByRiskClassification: [],
		aggregatedByAttendanceConditions: [],
		aggregatedByProfessionalsAndRiskClassificationStatus: [],
		averageTimeInSecondsByProfessional: [],
		averageTimeInSeconds: 0,
		averageWaitTimeInSeconds: 0,
		maxTimeInSeconds: 0,
		minTimeInSeconds: 0,
		maxWaitTimeInSeconds: 0,
		minWaitTimeInSeconds: 0,
		maxTimeAttendanceDetails: {
			attendance_id: 0,
			patient_name: "",
			patient_cpf: "",
			attendance_date: "",
			risk_classification_name: "",
			risk_classification_color: "",
		},
		minTimeAttendanceDetails: {
			attendance_id: 0,
			patient_name: "",
			patient_cpf: "",
			attendance_date: "",
			risk_classification_name: "",
			risk_classification_color: "",
		},
		maxWaitTimeAttendanceDetails: {
			attendance_id: 0,
			patient_name: "",
			patient_cpf: "",
			attendance_date: "",
			risk_classification_name: "",
			risk_classification_color: "",
		},
		minWaitTimeAttendanceDetails: {
			attendance_id: 0,
			patient_name: "",
			patient_cpf: "",
			attendance_date: "",
			risk_classification_name: "",
			risk_classification_color: "",
		},
		evasions: 0,
		totalAttendancesUnclassified: 0,
		totalAttendancesClassifiedOnce: 0,
		totalAttendancesClassifiedMoreThanOnce: 0,
		totalAttendances: 0,
	};

	if (!data) {
		return defaultData;
	}

	// Validar e garantir que todas as propriedades de array sejam arrays válidos
	return {
		aggregatedByRiskClassification: Array.isArray(data.aggregatedByRiskClassification)
			? data.aggregatedByRiskClassification
			: [],
		aggregatedByPeriodAndRiskClassificationStatus: Array.isArray(
			data.aggregatedByPeriodAndRiskClassificationStatus
		)
			? data.aggregatedByPeriodAndRiskClassificationStatus
			: [],
		aggregatedByHealthUnitAndRiskClassification: Array.isArray(
			data.aggregatedByHealthUnitAndRiskClassification
		)
			? data.aggregatedByHealthUnitAndRiskClassification
			: [],
		aggregatedByGender: Array.isArray(data.aggregatedByGender) ? data.aggregatedByGender : [],
		averageTimeInSecondsByRiskClassification: Array.isArray(
			data.averageTimeInSecondsByRiskClassification
		)
			? data.averageTimeInSecondsByRiskClassification
			: [],
		averageWaitTimeInSecondsByRiskClassification: Array.isArray(
			data.averageWaitTimeInSecondsByRiskClassification
		)
			? data.averageWaitTimeInSecondsByRiskClassification
			: [],
		aggregatedByAttendanceConditions: Array.isArray(data.aggregatedByAttendanceConditions)
			? data.aggregatedByAttendanceConditions
			: [],
		aggregatedByProfessionalsAndRiskClassificationStatus: Array.isArray(
			data.aggregatedByProfessionalsAndRiskClassificationStatus
		)
			? data.aggregatedByProfessionalsAndRiskClassificationStatus
			: [],
		averageTimeInSecondsByProfessional: Array.isArray(data.averageTimeInSecondsByProfessional)
			? data.averageTimeInSecondsByProfessional
			: [],
		averageTimeInSeconds: data.averageTimeInSeconds ?? 0,
		averageWaitTimeInSeconds: data.averageWaitTimeInSeconds ?? 0,
		maxTimeInSeconds: data.maxTimeInSeconds ?? 0,
		minTimeInSeconds: data.minTimeInSeconds ?? 0,
		maxWaitTimeInSeconds: data.maxWaitTimeInSeconds ?? 0,
		minWaitTimeInSeconds: data.minWaitTimeInSeconds ?? 0,
		maxTimeAttendanceDetails:
			data.maxTimeAttendanceDetails ?? defaultData.maxTimeAttendanceDetails,
		minTimeAttendanceDetails:
			data.minTimeAttendanceDetails ?? defaultData.minTimeAttendanceDetails,
		maxWaitTimeAttendanceDetails:
			data.maxWaitTimeAttendanceDetails ?? defaultData.maxWaitTimeAttendanceDetails,
		minWaitTimeAttendanceDetails:
			data.minWaitTimeAttendanceDetails ?? defaultData.minWaitTimeAttendanceDetails,
		evasions: data.evasions ?? 0,
		totalAttendancesUnclassified: data.totalAttendancesUnclassified ?? 0,
		totalAttendancesClassifiedOnce: data.totalAttendancesClassifiedOnce ?? 0,
		totalAttendancesClassifiedMoreThanOnce: data.totalAttendancesClassifiedMoreThanOnce ?? 0,
		totalAttendances: data.totalAttendances ?? 0,
	} as RiskClassificationDashboardData;
}
